import interpolateComponents from '@automattic/interpolate-components';
import { ReactElement, ReactNode, useCallback } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiNotification } from 'api/notification/types/ApiNotification';
import { GradfairCalendarNotificationContent } from 'components/NotificationList/content/GradfairCalendarNotificationContent';
import { GradfairNotificationContent } from 'components/NotificationList/content/GradfairNotificationContent';
import { Text } from 'components/Text/Text';
import { viewUserProfileRoute } from 'modules/userProfile/routing/userProfile.routes';
import { fontWeights } from 'theme/theme';
import { MappedLocalNotification } from 'types/LocalNotification/MappedLocalNotification';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from 'utils/constants/general/listingTypeHumanNameMapTitle';

type Props = {
  notification: ApiNotification | MappedNotification | MappedLocalNotification;
  render: (args: {
    to: string;
    eventName: string;
    eventProperties: Record<string, unknown>;
    className?: string;
    children: ReactNode;
  }) => ReactElement;
};

export function NotificationListItemContent({ notification, render }: Props) {
  const truncateText = useCallback(
    (title: string, length: number = 70) =>
      title.length > length ? title.slice(0, length).concat('...') : title,
    [],
  );
  switch (notification.type) {
    case 'OPENWEB_REPLIED_COMMENT': {
      return render({
        to: notification.data.url,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          comment_id: notification.data.comment_id,
        },
        children: (
          <>
            <Text fontWeight={fontWeights.normal}>
              {interpolateComponents({
                mixedString: sprintf(
                  getText(
                    '{{strong}}@%(replier_username)s{{/strong}} replied to your comment',
                  ),
                  { replier_username: notification.data.replier_username },
                ),
                components: {
                  strong: <strong />,
                },
              })}
            </Text>
            {notification.data.reply_text && (
              <Text color="lightContentGrey" marginTop={14} lineHeight={1}>
                &quot;{truncateText(notification.data.reply_text, 40)}&quot;
              </Text>
            )}
          </>
        ),
      });
    }
    case 'OPENWEB_LIKED_COMMENT': {
      return render({
        to: notification.data.url,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          comment_id: notification.data.comment_id,
        },
        children: (
          <>
            {notification.data.liker_usernames.length === 1 && (
              <Text fontWeight={fontWeights.normal}>
                {interpolateComponents({
                  mixedString: sprintf(
                    getText(
                      '{{strong}}@%(liker_username)s{{/strong}} liked your comment',
                    ),
                    { liker_username: notification.data.liker_usernames[0] },
                  ),
                  components: {
                    strong: <strong />,
                  },
                })}
              </Text>
            )}
            {notification.data.liker_usernames.length > 1 && (
              <Text fontWeight={fontWeights.normal}>
                {sprintf(getText('%(num_likers)d people liked your comment'), {
                  num_likers: notification.data.liker_usernames.length,
                })}
              </Text>
            )}
            {notification.data.comment_text && (
              <Text color="lightContentGrey" marginTop={14} lineHeight={1}>
                &quot;{truncateText(notification.data.comment_text, 40)}&quot;
              </Text>
            )}
          </>
        ),
      });
    }
    case 'OPENWEB_USER_MENTIONED': {
      return render({
        to: notification.data.url,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          comment_id: notification.data.comment_id,
        },
        children: (
          <>
            {notification.data.mentioner_username && (
              <Text fontWeight={fontWeights.normal}>
                {interpolateComponents({
                  mixedString: sprintf(
                    getText(
                      '{{strong}}@%(mentioner_username)s{{/strong}} mentioned you in a comment',
                    ),
                    {
                      mentioner_username: notification.data.mentioner_username,
                    },
                  ),
                  components: {
                    strong: <strong />,
                  },
                })}
              </Text>
            )}
            {notification.data.comment_text && (
              <Text color="lightContentGrey" marginTop={14} lineHeight={1}>
                &quot;{truncateText(notification.data.comment_text, 40)}&quot;
              </Text>
            )}
          </>
        ),
      });
    }
    case 'CONFIGURED_NOTIFICATION': {
      return render({
        to: notification.data.details.url,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.data.details.analyticsName,
        },
        children: (
          <>
            {notification.data.details.headline && (
              <Text fontWeight={fontWeights.semiBold}>
                {notification.data.details.headline}
              </Text>
            )}
            <Text fontWeight={fontWeights.normal}>
              {notification.data.details.body}
            </Text>
          </>
        ),
      });
    }
    case 'NEW_APPLICATIONS_BY_LISTING':
      return render({
        to: `/admin/${
          notification.about.id
        }/${notification.data.listing.type.toLowerCase()}s/${notification.data.listing.id}`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          listing_id: notification.data.listing.id,
          listing_type: notification.data.listing.type,
          listing_name: notification.data.listing.name,
          org_id: notification.about.id,
          application_count: notification.data.count,
        },
        children: (
          <>
            <Text fontWeight={fontWeights.semiBold}>
              {notification.data.listing.name}
            </Text>
            <Text fontWeight={fontWeights.normal}>
              {interpolateComponents({
                mixedString: sprintf(
                  notification.data.count > 1
                    ? getText(
                        '{{strong}}%(count)s{{/strong}} unread applications',
                      )
                    : getText(
                        '{{strong}}%(count)s{{/strong}} unread application',
                      ),
                  {
                    count: notification.data.count,
                  },
                ),
                components: {
                  strong: <strong />,
                },
              })}
            </Text>
          </>
        ),
      });

    case 'MENTION_LISTING_APPLICATION': {
      const title = truncateText(notification.data.listingApplication.name);

      return render({
        to: `/admin/${notification.about.id}/applications/${notification.data.listingApplication.id}`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          application_id: notification.data.listingApplication.id,
          listing_id: notification.data.listing.id,
          listing_type: notification.data.listing.type,
          listing_name: notification.data.listing.name,
          org_id: notification.about.id,
        },
        children: interpolateComponents({
          mixedString: sprintf(
            getText(
              "{{strong}}%(name)s{{/strong}} mentioned you: {{strong}}%(title)s{{/strong}}'s %(type)s application",
            ),
            {
              name: notification.data.entry.byUser.name,
              title,
              type: LISTING_TYPE_HUMAN_NAME_MAP_TITLE[
                notification.data.listing.type
              ].toLowerCase(),
            },
          ),
          components: {
            strong: <strong />,
          },
        }),
      });
    }

    case 'MENTION_SUGGESTED_APPLICANT': {
      const title = truncateText(notification.data.suggestedApplicant.name);

      return render({
        to: `/admin/${notification.about.id}/jobs/${notification.data.listing.id}/recommended-candidates/${notification.data.suggestedApplicant.id}`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          suggested_applicant_id: notification.data.suggestedApplicant.id,
          listing_id: notification.data.listing.id,
          listing_type: notification.data.listing.type,
          listing_name: notification.data.listing.name,
          org_id: notification.about.id,
        },
        children: interpolateComponents({
          mixedString: sprintf(
            getText(
              "{{strong}}%(name)s{{/strong}} mentioned you: {{strong}}%(title)s{{/strong}}'s resume",
            ),
            {
              name: notification.data.entry.byUser.name,
              title,
            },
          ),
          components: {
            strong: <strong />,
          },
        }),
      });
    }

    case 'NEW_SUGGESTED_APPLICANTS_BY_JOB': {
      const title = truncateText(notification.data.job.name);
      return render({
        to: `/admin/${notification.about.id}/jobs/${notification.data.job.id}/recommended-candidates`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          listing_id: notification.data.job.id,
          listing_type: 'JOB',
          listing_name: notification.data.job.name,
          org_id: notification.about.id,
          candidate_count: notification.data.count,
        },
        children: (
          <>
            <Text fontWeight={fontWeights.semiBold}>{title}</Text>
            <Text fontWeight={fontWeights.normal}>
              {interpolateComponents({
                mixedString: sprintf(
                  getText(
                    '{{strong}}%(count)d{{/strong}} new recommended resumes',
                  ),
                  {
                    count: notification.data.count,
                  },
                ),
                components: {
                  strong: <strong />,
                },
              })}
            </Text>
          </>
        ),
      });
    }

    case 'ORG_ADMIN_USER_DROPDOWN_ATS_CTA': {
      return render({
        to: '/applicant-tracker',
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'APPLICANT_TRACKER_AD',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {interpolateComponents({
              mixedString: sprintf(
                getText(
                  'Try our applicant management tool, included with any listing. {{strong}}Learn More{{/strong}}',
                ),
              ),
              components: {
                strong: <strong />,
              },
            })}
          </Text>
        ),
      });
    }

    case 'SALARY_SURVEY_CTA': {
      return render({
        to: '/nonprofit-salary-explorer',
        eventName: 'User Menu Notification',
        eventProperties: {
          cta_type: 'Link',
          button_text: 'Take Our Salary Survey',
          url: '/nonprofit-salary-explorer',
          context: 'User Menu',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {getText('Take Our Salary Survey')}
          </Text>
        ),
      });
    }

    case 'OFFICE_HOURS': {
      return render({
        to: 'https://idealist-org.zoom.us/webinar/register/1017104520689/WN_qgE2VESZSIGAC02uulzZag',
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'OFFICE_HOURS',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            &#x1F5D3;
            {getText(
              ' Join us on 4/24 for a virtual event! Hear more about job seeking tools & ask any/all questions.',
            )}
          </Text>
        ),
      });
    }

    case 'OFFICE_HOURS_07_2024': {
      return render({
        to: 'https://idealist-org.zoom.us/webinar/register/1017104520689/WN_A6O-JdOjQGuLFwvqPWuM8A',
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'OFFICE_HOURS_07_2024',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {getText(
              '🗓️ Join us on 7/11 for a virtual event! Hear more about job seeking tools & ask any/all questions.',
            )}
          </Text>
        ),
      });
    }

    case 'RESUME_WEBINAR': {
      return render({
        to: 'https://idealist-org.zoom.us/webinar/register/3417187290880/WN_-qgAkIcxS42Q9BGXKu6rkw',
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'RESUME_WEBINAR',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {getText(
              '📄 Join us for a free resume webinar on 7/24 - Learn how to make your resume stand out',
            )}
          </Text>
        ),
      });
    }

    case 'ES_SURVEY': {
      return render({
        to: 'https://www.idealist.org/data/s3-uploads/2f512547064640d9be7e839c407727fa',
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'ES_SURVEY',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {getText(
              'Resultados de Encuesta: ¿Qué motiva y qué frena a las personas a la Participación Ciudadana en Iberoamérica?',
            )}
          </Text>
        ),
      });
    }

    case 'INTERVIEW_WEBINAR_08_2024': {
      return render({
        to: 'https://idealist-org.zoom.us/webinar/register/5417187381775/WN_eOPigDgxTF687t5IQxHrtg',
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'INTERVIEW_WEBINAR_08_2024',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {getText(
              'Want to ace your next job interview? RSVP for our 8/15 webinar',
            )}
          </Text>
        ),
      });
    }

    case 'GF_2024_TEXT': {
      return render({
        to: 'https://www.idealist.org/grad-schools/attend?utm_source=ad1&utm_medium=site&utm_campaign=en_users_2024grf_register',
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'GF_2024_TEXT',
        },
        children: <GradfairNotificationContent />,
      });
    }

    case 'GF_2024_CALENDAR': {
      return render({
        to: 'https://www.idealist.org/grad-schools/attend?utm_source=ad2&utm_medium=site&utm_campaign=en_users_2024grf_register',
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'GF_2024_CALENDAR',
        },
        children: <GradfairCalendarNotificationContent />,
      });
    }

    case 'ADMIN_SURVEY_2024': {
      return render({
        to: 'https://www.surveymonkey.com/r/talent_24_site',
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'ADMIN_SURVEY_2024',
        },
        children: (
          <>
            <Text fontWeight={fontWeights.semiBold}>
              {getText('We want your feedback')}
            </Text>
            <Text fontWeight={fontWeights.normal}>
              {getText(
                'Complete our survey for a chance to win a $100 gift card',
              )}
            </Text>
          </>
        ),
      });
    }

    case 'NEW_FOLLOWER': {
      return render({
        to: viewUserProfileRoute.with({
          username: notification.data.user.username,
        }),
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'NEW_FOLLOWER',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {sprintf(getText('@%(username)s started following you'), {
              username: notification.data.user.username,
            })}
          </Text>
        ),
      });
    }

    case 'NEW_RSVP_BY_LISTING':
      return render({
        to: notification.about
          ? `/admin/${notification.about.id}/events/${notification.data.event.id}`
          : `/dashboard/events/${notification.data.event.id}`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: notification.type,
          listing_id: notification.data.event.id,
          listing_type: notification.data.event.type,
          listing_name: notification.data.event.name,
          org_id: notification.about?.id,
          application_count: notification.data.count,
        },
        children: (
          <>
            <Text fontWeight={fontWeights.semiBold}>
              {notification.data.event.name}
            </Text>
            <Text fontWeight={fontWeights.normal}>
              {interpolateComponents({
                mixedString: sprintf(
                  notification.data.count > 1
                    ? getText(
                        '{{strong}}%(count)s{{/strong}} new RSVPs for your event',
                      )
                    : getText(
                        '{{strong}}%(count)s{{/strong}} new RSVP for your event',
                      ),
                  {
                    count: notification.data.count,
                  },
                ),
                components: {
                  strong: <strong />,
                },
              })}
            </Text>
          </>
        ),
      });

    case 'EXPLORER_RESOURCE': {
      return render({
        to: `/en/nonprofit-salary-explorer/whats-next`,
        eventName: 'User Menu Notification',
        eventProperties: {
          notification_type: 'EXPLORER_RESOURCE',
        },
        children: (
          <Text fontWeight={fontWeights.normal}>
            {getText(
              'Free resources for using our salary data to advance your career.',
            )}
          </Text>
        ),
      });
    }

    default:
      return null;
  }
}
